"use client";

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api-client";

const REQUEST_TIMEOUT_MS = 20_000;

/** Keep the request and its feedback mounted while the listing menu closes. */
export function useListingDirectPick({
  listingId,
  enabled,
}: {
  listingId: number;
  enabled: boolean;
}) {
  const t = useTranslations("saas.listing.header.actions.directPick");
  const router = useRouter();
  const utils = api.useUtils();
  const toastId = `direct-pick:${listingId}`;
  const status = api.os.deals.getDirectPickStatus.useQuery(
    { listingId },
    { enabled, staleTime: 0, retry: false },
  );
  const pick = useMutation({
    mutationFn: async (_intent: { openPipeline: boolean }) => {
      const controller = new AbortController();
      let timeout: ReturnType<typeof setTimeout> | undefined;
      try {
        // A batch may contain other requests that cannot be aborted yet.
        // Bound this action's feedback independently of the shared transport.
        return await Promise.race([
          utils.client.os.deals.directPickListing.mutate(
            { listingId },
            { signal: controller.signal },
          ),
          new Promise<never>((_resolve, reject) => {
            timeout = setTimeout(() => {
              reject(new Error(t("unconfirmed")));
              controller.abort();
            }, REQUEST_TIMEOUT_MS);
          }),
        ]);
      } finally {
        clearTimeout(timeout);
      }
    },
    retry: false,
    onMutate: ({ openPipeline }) => {
      toast.loading(openPipeline ? t("opening") : t("adding"), { id: toastId });
    },
    onSuccess: (result, { openPipeline }) => {
      toast.success(result.created ? t("added") : t("alreadyAdded"), {
        id: toastId,
        duration: 10_000,
        action: {
          label: t("openPipeline"),
          onClick: () => router.push(result.href),
        },
      });
      utils.os.deals.getDirectPickStatus.setData(
        { listingId },
        { eligible: true, dealId: result.dealId },
      );
      // Cache refresh is independent of confirmation and navigation.
      void utils.os.deals.listDeals.invalidate().catch(() => undefined);
      if (openPipeline) router.push(result.href);
    },
    onError: (error) => {
      toast.error(error.message || t("failed"), { id: toastId });
    },
  });

  return {
    visible: Boolean(status.data?.eligible),
    isPending: pick.isPending,
    label: pick.isPending
      ? t("adding")
      : status.data?.dealId
        ? t("open")
        : t("add"),
    select: () => {
      if (!pick.isPending)
        pick.mutate({ openPipeline: Boolean(status.data?.dealId) });
    },
  };
}
